import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import services from '../../services'
import SeletorDeLoja from '../../novo/loja/SeletorDeLoja'
import { Loja } from '../../novo/loja/geo'
import { chaveRecentes, lembrarLoja } from '../../novo/loja/recentes'
import RelatorioHtmlFrame, { ContextoRelatorio, ResultadoConsulta } from '../RelatorioHtmlFrame'

interface Props {
  dashboardId: number
  ambienteId: number
}

/** Presente quando o relatório declara seletor_loja: o portal oferece escolher a loja pela localização antes de abrir. */
interface SeletorLojaConfig {
  /** O id do filtro da tela que recebe a loja. */
  filtro: string
  raioInicialM: number
  obrigatorio: boolean
}

interface Documento {
  html: string
  contexto: ContextoRelatorio
  seletorLoja?: SeletorLojaConfig | null
}

// No celular o seletor abre sozinho; no computador, só quando a pessoa pede (?seletor=1) ou o link traz uma loja.
const emCelular = () => typeof window !== 'undefined' && !!window.matchMedia?.('(max-width: 768px)').matches

/**
 * O relatório HTML como quem visualiza vê: só a versão publicada, e toda consulta
 * passando pelo servidor com os filtros obrigatórios deste ambiente e deste usuário.
 *
 * Com seletor de loja, a loja escolhida chega ao relatório como filtro da tela já marcado. Ela só vale enquanto a página está aberta:
 * recarregar ou voltar depois pede a escolha de novo. Um link com ?loja=10482 vale uma vez (a loja é lida e o endereço é limpo).
 */
const RelatorioHtmlViewer = ({ dashboardId, ambienteId }: Props) => {
  const [documento, setDocumento] = useState<Documento | null>(null)
  const [erro, setErro] = useState('')
  const [params, setParams] = useSearchParams()
  const [lojas, setLojas] = useState<Loja[] | null>(null)
  const [erroLojas, setErroLojas] = useState('')
  // "Ver todas as lojas": abre o relatório sem recorte de loja, mesmo com o seletor ligado.
  const [verTodas, setVerTodas] = useState(false)
  // A loja escolhida mora só aqui, na memória da página: não vai para a URL nem para o navegador.
  const [escolhida, setEscolhida] = useState<Loja | null>(null)
  // "Trocar" abre o seletor mesmo no computador, onde ele não abre sozinho.
  const [forcar, setForcar] = useState(false)
  const [avisoLink, setAvisoLink] = useState<string | null>(null)

  useEffect(() => {
    let ativo = true
    setDocumento(null)
    setErro('')
    setLojas(null)
    setErroLojas('')
    setVerTodas(false)
    setEscolhida(null)
    setForcar(false)
    setAvisoLink(null)
    services.api.relatorioHtmlAbrir(dashboardId, ambienteId).then((r: any) => {
      if (!ativo) return
      if (r.fail) setErro(r.error)
      else setDocumento(r.data)
    })
    return () => {
      ativo = false
    }
  }, [dashboardId, ambienteId])

  const seletor = documento?.seletorLoja || null
  useEffect(() => {
    if (!seletor) return
    let ativo = true
    services.api.relatorioHtmlLojas(dashboardId, ambienteId).then((r: any) => {
      if (!ativo) return
      if (r.fail) setErroLojas(r.error || 'Tente de novo em instantes.')
      else setLojas(r.data?.lojas || [])
    })
    return () => {
      ativo = false
    }
    // Só a presença do seletor importa: o objeto novo a cada abertura não refaz a consulta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardId, ambienteId, !!seletor])

  const consultar = useCallback(
    async (consulta: unknown): Promise<ResultadoConsulta> => {
      const r: any = await services.api.relatorioHtmlDados(dashboardId, ambienteId, consulta)
      if (r.fail) throw new Error(r.error)
      return r.data
    },
    [dashboardId, ambienteId]
  )

  // O relatório abriu WhatsApp, telefone ou um site: vai para o log de ações (quem, quando, relatório e destino curto). Falha de rede não atrapalha.
  const registrarAbertura = useCallback((acao: { tipo: string; destino: string }) => {
    Promise.resolve(services.api.relatorioHtmlRegistrarAbertura(dashboardId, ambienteId, acao)).catch(() => undefined)
  }, [dashboardId, ambienteId])

  // Link com ?loja=: a loja é lida uma vez e o parâmetro sai do endereço, para recarregar não trazer a mesma loja de volta.
  const lojaDoLink = params.get('loja')
  useEffect(() => {
    if (!lojaDoLink || !lojas) return
    const achada = lojas.find(l => l.id === lojaDoLink)
    if (achada) { setEscolhida(achada); setAvisoLink(null) }
    else setAvisoLink(`A loja ${lojaDoLink} não está na sua lista. Escolha uma das lojas abaixo.`)
    const p = new URLSearchParams(params)
    p.delete('loja')
    setParams(p, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lojaDoLink, lojas])

  const escolher = (loja: Loja) => {
    lembrarLoja(chaveRecentes(dashboardId, ambienteId), loja.id)
    setEscolhida(loja)
    setAvisoLink(null)
    setVerTodas(false)
    setForcar(false)
    if (params.get('seletor')) {
      const p = new URLSearchParams(params)
      p.delete('seletor')
      setParams(p, { replace: true })
    }
  }
  const trocar = () => {
    setEscolhida(null)
    setVerTodas(false)
    setForcar(true)
  }

  if (erro) {
    return <div style={{ padding: 32, color: '#b91c1c' }}>{erro}</div>
  }

  if (!documento) {
    return <div style={{ padding: 32, color: '#64748b' }}>Carregando relatório…</div>
  }

  const frame = (filtrosAplicados?: Record<string, Array<string | number | boolean>>) => (
    <RelatorioHtmlFrame html={documento.html} contexto={documento.contexto} consultar={consultar}
                        chaveFiltros={`portal.relatorio-html.filtros.${dashboardId}.${ambienteId}`} filtrosAplicados={filtrosAplicados}
                        aoAbrir={registrarAbertura} />
  )

  // Sem seletor, o relatório abre como sempre.
  if (!seletor) return frame()

  const loja = escolhida
  const vaiMostrar = !loja && !verTodas && (emCelular() || forcar || params.get('seletor') === '1' || !!lojaDoLink || !!avisoLink)

  if (vaiMostrar) {
    if (!lojas && !erroLojas) return <div style={{ padding: 32, color: '#64748b' }}>Carregando as lojas…</div>
    return (
      <SeletorDeLoja
        lojas={lojas || []}
        raioInicialM={seletor.raioInicialM}
        obrigatorio={seletor.obrigatorio}
        chaveRecentes={chaveRecentes(dashboardId, ambienteId)}
        aoEscolher={escolher}
        aoVerTodas={() => setVerTodas(true)}
        aviso={avisoLink}
        erro={erroLojas || null}
      />
    )
  }

  if (!loja) {
    // Sem loja escolhida e sem seletor (computador, ou "Ver todas"): a lista vazia limpa a loja que a pessoa deixou guardada.
    return <div className="rl-viewer"><div className="rl-viewer-corpo" key="todas">{frame({ [seletor.filtro]: [] })}</div></div>
  }

  const detalhe = [loja.cidade && loja.uf ? `${loja.cidade}/${loja.uf}` : loja.cidade || loja.uf, loja.bandeira].filter(Boolean).join(' · ')
  return (
    <div className="rl-viewer">
      <div className="rl-loja">
        <span className="rl-loja-nome"><small>Loja{detalhe ? ` · ${detalhe}` : ''}</small><b>{loja.nome}</b></span>
        <button type="button" onClick={trocar}>Trocar</button>
      </div>
      <div className="rl-viewer-corpo" key={loja.id}>
        {frame({ [seletor.filtro]: loja.valor == null ? [] : [loja.valor] })}
      </div>
    </div>
  )
}

export default RelatorioHtmlViewer
